import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import * as z from "zod";
import { Party, PartyCreate, PartyUpdate, PartyGroup } from "@/app/types/party";
import { Site } from "@/app/types/site";
import { Button } from "@/components/ui/button";
import { useAuthStore } from "@/app/store/authStore";
import { PartySelector } from "@/app/components/common/PartySelector";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Checkbox } from "@/components/ui/checkbox";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";

// Form schema using zod
const formSchema = z.object({
  name: z.string().min(1, "Name is required"),
  phone: z.string().optional().nullable(),
  email: z.string().optional().nullable(),
  gstin: z.string().optional().nullable(),
  gst_type: z.string().optional().nullable(),
  state: z.string().optional().nullable(),
  billing_address: z.string().optional().nullable(),
  shipping_address: z.string().optional().nullable(),
  has_separate_shipping: z.boolean().default(false),
  geo_location: z.string().optional().nullable(),
  opening_balance: z.union([z.string(), z.number()]).optional().nullable(),
  opening_balance_date: z.string().optional().nullable(),
  credit_limit_type: z.enum(["no_limit", "custom_limit"]).default("no_limit"),
  credit_limit_amount: z.union([z.string(), z.number()]).optional().nullable(),
  party_group_id: z.number().optional().nullable(),
  site_id: z.number().optional().nullable(),
  responsible_party_id: z.number().optional().nullable(),
});

// GST types for select options
const gstTypes = [
  { value: "Registered", label: "Registered" },
  { value: "Unregistered/Consumer", label: "Unregistered/Consumer" },
];

// Indian states for select options
const indianStates = [
  "Andhra Pradesh",
  "Arunachal Pradesh",
  "Assam",
  "Bihar",
  "Chhattisgarh",
  "Goa",
  "Gujarat",
  "Haryana",
  "Himachal Pradesh",
  "Jharkhand",
  "Karnataka",
  "Kerala",
  "Madhya Pradesh",
  "Maharashtra",
  "Manipur",
  "Meghalaya",
  "Mizoram",
  "Nagaland",
  "Odisha",
  "Punjab",
  "Rajasthan",
  "Sikkim",
  "Tamil Nadu",
  "Telangana",
  "Tripura",
  "Uttar Pradesh",
  "Uttarakhand",
  "West Bengal",
  "Andaman and Nicobar Islands",
  "Chandigarh",
  "Dadra and Nagar Haveli and Daman and Diu",
  "Delhi",
  "Jammu and Kashmir",
  "Ladakh",
  "Lakshadweep",
  "Puducherry",
];

interface PartyFormProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSubmit: (data: PartyCreate | PartyUpdate) => void;
  defaultValues?: Partial<Party>;
  isLoading?: boolean;
  mode: "create" | "edit";
  partyGroups: PartyGroup[];
  sites: Site[];
  defaultSiteId?: number | null;
}

const PartyForm: React.FC<PartyFormProps> = ({
  open,
  onOpenChange,
  onSubmit,
  defaultValues,
  isLoading = false,
  mode,
  partyGroups,
  sites,
  defaultSiteId,
}) => {
  // Get current user from auth store
  const user = useAuthStore((state) => state.user);
  const isSuperAdmin = user?.username === 'superadmin';

  // Initialize the form
  const form = useForm<z.infer<typeof formSchema>>({
    resolver: zodResolver(formSchema),
    defaultValues: {
      name: defaultValues?.name || "",
      phone: defaultValues?.phone || "",
      email: defaultValues?.email || "",
      gstin: defaultValues?.gstin || "",
      gst_type: defaultValues?.gst_type || "",
      state: defaultValues?.state || "Tamil Nadu",
      billing_address: defaultValues?.billing_address || "",
      shipping_address: defaultValues?.shipping_address || "",
      has_separate_shipping: defaultValues?.has_separate_shipping || false,
      geo_location: defaultValues?.geo_location || "",
      opening_balance: defaultValues?.opening_balance || "",
      opening_balance_date: defaultValues?.opening_balance_date || "",
      credit_limit_type: defaultValues?.credit_limit_type || "no_limit",
      credit_limit_amount: defaultValues?.credit_limit_amount || "",
      party_group_id: defaultValues?.party_group_id || null,
      site_id: defaultValues?.site_id || (mode === "create" ? defaultSiteId : null),
      responsible_party_id: defaultValues?.responsible_party_id || null,
    },
  });

  // Update shipping address when billing address changes if not separate
  const hasSeparateShipping = form.watch("has_separate_shipping");
  const billingAddress = form.watch("billing_address");

  // Handle form submission
  const handleSubmit = form.handleSubmit((data) => {
    onSubmit(data);
  });

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[600px] max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>
            {mode === "create" ? "Add New Party" : "Edit Party"}
          </DialogTitle>
          <DialogDescription>
            Fill in the details to {mode === "create" ? "create a new" : "update this"} party.
          </DialogDescription>
        </DialogHeader>

        <Form {...form}>
          <form onSubmit={handleSubmit} className="space-y-6">
            {/* Basic Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Basic Information</h3>
              
              <FormField
                control={form.control}
                name="name"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Name*</FormLabel>
                    <FormControl>
                      <Input {...field} placeholder="Enter party name" />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="phone"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Phone</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter phone number" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="email"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Email</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter email address" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="party_group_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Party Group</FormLabel>
                      <Select
                        value={field.value?.toString() || "none"}
                        onValueChange={(value) => field.onChange(value === "none" ? null : parseInt(value))}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a group" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {partyGroups.map((group) => (
                            <SelectItem key={group.id} value={group.id.toString()}>
                              {group.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="site_id"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Site</FormLabel>
                      <Select
                        value={field.value?.toString() || "none"}
                        onValueChange={(value) => field.onChange(value === "none" ? null : parseInt(value))}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select a site" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="none">None</SelectItem>
                          {sites.map((site) => (
                            <SelectItem key={site.id} value={site.id.toString()}>
                              {site.name}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="responsible_party_id"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Responsible Party</FormLabel>
                    <PartySelector
                      value={field.value ?? undefined}
                      onChange={(value) => field.onChange(value ?? null)}
                      placeholder="Select responsible employee..."
                      filterPartyGroup="EMPLOYEE"
                      disabled={isLoading}
                    />
                    <FormDescription>
                      Assign an employee as the responsible party for this customer/vendor
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* GST Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">GST Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="gstin"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>GSTIN</FormLabel>
                      <FormControl>
                        <Input {...field} placeholder="Enter GSTIN" />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="gst_type"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>GST Type</FormLabel>
                      <Select
                        value={field.value || "not_specified"}
                        onValueChange={(value) => field.onChange(value === "not_specified" ? null : value)}
                      >
                        <FormControl>
                          <SelectTrigger>
                            <SelectValue placeholder="Select GST type" />
                          </SelectTrigger>
                        </FormControl>
                        <SelectContent>
                          <SelectItem value="not_specified">Not Specified</SelectItem>
                          {gstTypes.map((type) => (
                            <SelectItem key={type.value} value={type.value}>
                              {type.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="state"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>State</FormLabel>
                    <Select
                      value={field.value || "not_specified"}
                      onValueChange={(value) => field.onChange(value === "not_specified" ? null : value)}
                    >
                      <FormControl>
                        <SelectTrigger>
                          <SelectValue placeholder="Select state" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="not_specified">Not Specified</SelectItem>
                        {indianStates.map((state) => (
                          <SelectItem key={state} value={state}>
                            {state}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Address Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Address Information</h3>
              
              <FormField
                control={form.control}
                name="billing_address"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Billing Address</FormLabel>
                    <FormControl>
                      <Textarea
                        {...field}
                        placeholder="Enter billing address"
                        rows={3}
                      />
                    </FormControl>
                    <FormMessage />
                  </FormItem>
                )}
              />

              <FormField
                control={form.control}
                name="has_separate_shipping"
                render={({ field }) => (
                  <FormItem className="flex flex-row items-start space-x-3 space-y-0 rounded-md border p-4">
                    <FormControl>
                      <Checkbox
                        checked={field.value}
                        onCheckedChange={field.onChange}
                      />
                    </FormControl>
                    <div className="space-y-1 leading-none">
                      <FormLabel>Separate Shipping Address</FormLabel>
                      <FormDescription>
                        Check this if the shipping address is different from the billing address
                      </FormDescription>
                    </div>
                  </FormItem>
                )}
              />

              {hasSeparateShipping && (
                <FormField
                  control={form.control}
                  name="shipping_address"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Shipping Address</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Enter shipping address"
                          rows={3}
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}

              <FormField
                control={form.control}
                name="geo_location"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Geographic Location</FormLabel>
                    <FormControl>
                      <Input
                        {...field}
                        placeholder="Enter location (e.g., 13.0827,80.2707)"
                      />
                    </FormControl>
                    <FormDescription>
                      Enter coordinates as latitude, longitude
                    </FormDescription>
                    <FormMessage />
                  </FormItem>
                )}
              />
            </div>

            {/* Financial Information */}
            <div className="space-y-4">
              <h3 className="text-lg font-medium">Financial Information</h3>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <FormField
                  control={form.control}
                  name="opening_balance"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Opening Balance</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          step="0.01"
                          placeholder="0.00"
                          disabled={mode === "edit"}
                        />
                      </FormControl>
                      {mode === "edit" && (
                        <FormDescription>Opening balance cannot be modified after creation</FormDescription>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />

                <FormField
                  control={form.control}
                  name="opening_balance_date"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>As of Date</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="date"
                          placeholder="Select date"
                        />
                      </FormControl>
                      <FormMessage />
                    </FormItem>
                  )}
                />
              </div>

              <FormField
                control={form.control}
                name="credit_limit_type"
                render={({ field }) => (
                  <FormItem>
                    <FormLabel>Credit Limit</FormLabel>
                    <Select
                      value={field.value}
                      onValueChange={field.onChange}
                      disabled={!isSuperAdmin}
                    >
                      <FormControl>
                        <SelectTrigger disabled={!isSuperAdmin}>
                          <SelectValue placeholder="Select credit limit type" />
                        </SelectTrigger>
                      </FormControl>
                      <SelectContent>
                        <SelectItem value="no_limit">No Limit</SelectItem>
                        <SelectItem value="custom_limit">Custom Limit</SelectItem>
                      </SelectContent>
                    </Select>
                    {!isSuperAdmin && (
                      <FormDescription>Only superadmin can modify credit limits</FormDescription>
                    )}
                    <FormMessage />
                  </FormItem>
                )}
              />

              {form.watch("credit_limit_type") === "custom_limit" && (
                <FormField
                  control={form.control}
                  name="credit_limit_amount"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Credit Limit Amount</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="number"
                          step="0.01"
                          placeholder="Enter credit limit amount"
                          disabled={!isSuperAdmin}
                        />
                      </FormControl>
                      {!isSuperAdmin && (
                        <FormDescription>Only superadmin can modify credit limits</FormDescription>
                      )}
                      <FormMessage />
                    </FormItem>
                  )}
                />
              )}
            </div>

            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => onOpenChange(false)}>
                Cancel
              </Button>
              <Button type="submit" disabled={isLoading}>
                {isLoading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    {mode === "create" ? "Creating..." : "Updating..."}
                  </>
                ) : (
                  <>{mode === "create" ? "Create Party" : "Update Party"}</>
                )}
              </Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  );
};

export default PartyForm; 